import React, { useState, useEffect, useRef } from "react";
import "./css/LandingPage-Demo.css";

function LandingPageDemo({ onManageAccountClick }) {
  const [scamStage, setScamStage] = useState("idle");
  const [revealedFlags, setRevealedFlags] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [showRedFlags, setShowRedFlags] = useState(false);
  const [stats, setStats] = useState({
    scamsBlocked: 0,
    usersProtected: 0,
    threatsDetected: 0,
  });
  const [challengeScore, setChallengeScore] = useState(0);
  const [challengeActive, setChallengeActive] = useState(false);
  const [selectedFlags, setSelectedFlags] = useState([]);
  const [expandedScam, setExpandedScam] = useState(null);
  const [urgencyTimer, setUrgencyTimer] = useState(300);

  const fullMessage =
    "URGENT: Your package delivery failed. Confirm your address within 5 minutes to avoid return fees: https://usps-redelivery.net/confirm";

  // Animate statistics on mount
  useEffect(() => {
    const targets = {
      scamsBlocked: 12847,
      usersProtected: 8392,
      threatsDetected: 45621,
    };

    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;

      setStats({
        scamsBlocked: Math.floor(targets.scamsBlocked * progress),
        usersProtected: Math.floor(targets.usersProtected * progress),
        threatsDetected: Math.floor(targets.threatsDetected * progress),
      });

      if (step >= steps) clearInterval(timer);
    }, interval);

    return () => clearInterval(timer);
  }, []);

  // Auto-start scam demo after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setScamStage("typing");
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Typing animation for scam message
  useEffect(() => {
    if (scamStage === "typing" && messageText.length < fullMessage.length) {
      const timer = setTimeout(() => {
        setMessageText(fullMessage.slice(0, messageText.length + 1));
      }, 30);
      return () => clearTimeout(timer);
    } else if (
      scamStage === "typing" &&
      messageText.length === fullMessage.length
    ) {
      setScamStage("complete");
    }
  }, [scamStage, messageText, fullMessage]);

  // Urgency countdown timer
  useEffect(() => {
    if (scamStage === "complete" && urgencyTimer > 0) {
      const timer = setInterval(() => {
        setUrgencyTimer((prev) => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [scamStage, urgencyTimer]);

  const handleScamInteraction = (flagType) => {
    if (!revealedFlags.includes(flagType)) {
      setRevealedFlags([...revealedFlags, flagType]);
      setShowRedFlags(true);
    }
  };

  const startChallenge = () => {
    setChallengeActive(true);
    setSelectedFlags([]);
    setChallengeScore(0);
  };

  const selectFlag = (flagId) => {
    if (selectedFlags.includes(flagId)) {
      setSelectedFlags(selectedFlags.filter((id) => id !== flagId));
    } else if (selectedFlags.length < 3) {
      setSelectedFlags([...selectedFlags, flagId]);
    }
  };

  const submitChallenge = () => {
    const correctFlags = [1, 3, 5];
    const correct = selectedFlags.filter((id) => correctFlags.includes(id));
    const score = Math.floor((correct.length / 3) * 100);
    setChallengeScore(score);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const scamTypes = [
    {
      id: 1,
      title: "Phishing Emails",
      icon: "📧",
      description:
        "Fake emails impersonating trusted organizations to steal credentials",
      examples: [
        "Urgent account verification requests",
        "Prize/lottery winnings notifications",
        "Fake invoice or payment confirmations",
      ],
    },
    {
      id: 2,
      title: "Smishing (SMS)",
      icon: "💬",
      description: "Text message scams designed to create urgency and panic",
      examples: [
        "Package delivery failures",
        "Bank account security alerts",
        "Government agency warnings",
      ],
    },
    {
      id: 3,
      title: "Vishing (Voice)",
      icon: "📞",
      description: "Phone call scams using social engineering tactics",
      examples: [
        "Tech support impersonation",
        "IRS/tax authority threats",
        "Fake relative emergencies",
      ],
    },
    {
      id: 4,
      title: "Romance Scams",
      icon: "💔",
      description:
        "Building fake relationships to manipulate victims financially",
      examples: [
        "Online dating profile catfishing",
        "Emergency money requests",
        "Investment 'opportunities'",
      ],
    },
    {
      id: 5,
      title: "Investment Fraud",
      icon: "📈",
      description:
        "Fake investment opportunities promising unrealistic returns",
      examples: [
        "Cryptocurrency pump schemes",
        "Ponzi/pyramid schemes",
        "Fake real estate deals",
      ],
    },
    {
      id: 6,
      title: "Tech Support",
      icon: "🖥️",
      description: "Fake technical issues to gain remote access to devices",
      examples: [
        "Virus warning popups",
        "Microsoft/Apple imposters",
        "Router/internet scams",
      ],
    },
  ];

  const challengeElements = [
    { id: 1, text: "Suspicious shortened URL", isFlag: true },
    { id: 2, text: "Package tracking number", isFlag: false },
    { id: 3, text: "5-minute urgency deadline", isFlag: true },
    { id: 4, text: "Sender phone number", isFlag: false },
    { id: 5, text: "Threats of fees/penalties", isFlag: true },
    { id: 6, text: "Delivery service mentioned", isFlag: false },
  ];

  return (
    <div className="landing-demo">
      {/* Hero Section with Interactive Scam Demo */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Stop Scams
              <span className="title-highlight"> Before They Start</span>
            </h1>
            <p className="hero-subtitle">
              Learn to recognize and avoid scams through real-world interactive
              simulations. See what scammers don't want you to know.
            </p>
            <div className="hero-cta">
              <button
                className="cta-primary"
                onClick={onManageAccountClick}
                aria-label="Start your free trial"
              >
                Start Free Trial
              </button>
              <button
                className="cta-secondary"
                onClick={() => handleScamInteraction("all")}
                aria-label="Reveal all red flags"
              >
                <span className="icon">🔍</span> Reveal All Red Flags
              </button>
            </div>
          </div>

          <div className="scam-demo-container">
            <div className="phone-mockup">
              <div className="phone-header">
                <span className="time">12:34</span>
                <div className="phone-notch"></div>
                <div className="phone-status">
                  <span className="signal">📶</span>
                  <span className="battery">🔋</span>
                </div>
              </div>

              <div className="phone-screen">
                <div className="message-header">
                  <span className="back-arrow">←</span>
                  <div className="sender-info">
                    <span className="sender-name">USPS</span>
                    <span className="sender-number">+1 (855) 724-0218</span>
                  </div>
                </div>

                <div className="phone-messages">
                  {scamStage !== "idle" && (
                    <div className="message-bubble incoming">
                      <div className="message-content">
                        {messageText}
                        {scamStage === "typing" &&
                          messageText.length < fullMessage.length && (
                            <span className="typing-cursor">|</span>
                          )}
                      </div>
                      {scamStage === "complete" && (
                        <div className="message-time">Just now</div>
                      )}
                    </div>
                  )}

                  {scamStage === "idle" && (
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  )}
                </div>

                {scamStage === "complete" && (
                  <div className="urgency-banner">
                    <span className="urgency-icon">⚠️</span>
                    <span className="urgency-text">
                      Time remaining: {formatTime(urgencyTimer)}
                    </span>
                  </div>
                )}

                {/* Interactive Red Flag Overlays */}
                {showRedFlags && (
                  <>
                    <div
                      className="red-flag-marker flag-1"
                      onClick={() => handleScamInteraction("url")}
                    >
                      <div className="flag-icon">🚩</div>
                      {revealedFlags.includes("url") && (
                        <div className="flag-tooltip">
                          <strong>Suspicious URL</strong>
                          <p>
                            Real USPS uses usps.com, not lookalike domains. This
                            is a phishing link.
                          </p>
                        </div>
                      )}
                    </div>

                    <div
                      className="red-flag-marker flag-2"
                      onClick={() => handleScamInteraction("urgency")}
                    >
                      <div className="flag-icon">🚩</div>
                      {revealedFlags.includes("urgency") && (
                        <div className="flag-tooltip">
                          <strong>Artificial Urgency</strong>
                          <p>
                            Scammers create panic with short deadlines to
                            prevent critical thinking.
                          </p>
                        </div>
                      )}
                    </div>

                    <div
                      className="red-flag-marker flag-3"
                      onClick={() => handleScamInteraction("sender")}
                    >
                      <div className="flag-icon">🚩</div>
                      {revealedFlags.includes("sender") && (
                        <div className="flag-tooltip">
                          <strong>Unknown Sender</strong>
                          <p>
                            USPS sends tracking updates from known numbers and
                            official apps, not random numbers.
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="demo-instruction">
              {!showRedFlags ? (
                <p>
                  Click <strong>"Reveal All Red Flags"</strong> to see what
                  makes this a scam ↑
                </p>
              ) : (
                <p>
                  Click the <span className="flag-icon-inline">🚩</span> markers
                  to learn about each red flag
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Live Statistics Dashboard */}
      <section className="stats-section">
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-number">
              {stats.scamsBlocked.toLocaleString()}
            </div>
            <div className="stat-label">Scams Blocked This Week</div>
            <div className="stat-trend">↑ 23% from last week</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {stats.usersProtected.toLocaleString()}
            </div>
            <div className="stat-label">Users Protected</div>
            <div className="stat-trend">↑ Active learners</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {stats.threatsDetected.toLocaleString()}
            </div>
            <div className="stat-label">Threats Detected</div>
            <div className="stat-trend">↑ Real-time monitoring</div>
          </div>
        </div>
      </section>

      {/* Quick Challenge Section */}
      <section className="challenge-section">
        <div className="challenge-container">
          <h2 className="section-title">
            <span className="icon">🎯</span> 30-Second Challenge
          </h2>
          <p className="section-subtitle">
            Can you identify the 3 red flags in this scam message? Test your
            scam detection skills now.
          </p>

          {!challengeActive ? (
            <button className="challenge-start-btn" onClick={startChallenge}>
              Start Challenge
            </button>
          ) : (
            <div className="challenge-active">
              <div className="challenge-message">
                <p>"{fullMessage}"</p>
              </div>

              <div className="challenge-options">
                <p className="challenge-instruction">
                  Select 3 elements that indicate this is a scam:
                </p>
                <div className="challenge-grid">
                  {challengeElements.map((element) => (
                    <button
                      key={element.id}
                      className={`challenge-option ${
                        selectedFlags.includes(element.id) ? "selected" : ""
                      } ${
                        challengeScore > 0
                          ? element.isFlag
                            ? "correct"
                            : "incorrect"
                          : ""
                      }`}
                      onClick={() => selectFlag(element.id)}
                      disabled={challengeScore > 0}
                    >
                      {element.text}
                      {challengeScore > 0 && element.isFlag && (
                        <span className="check-icon">✓</span>
                      )}
                    </button>
                  ))}
                </div>

                {selectedFlags.length === 3 && challengeScore === 0 && (
                  <button
                    className="challenge-submit-btn"
                    onClick={submitChallenge}
                  >
                    Submit Answer
                  </button>
                )}

                {challengeScore > 0 && (
                  <div className="challenge-result">
                    <div
                      className={`score-display ${
                        challengeScore === 100 ? "perfect" : ""
                      }`}
                    >
                      Your Score: {challengeScore}/100
                    </div>
                    {challengeScore === 100 ? (
                      <p className="result-message success">
                        🎉 Perfect! You've identified all the red flags. You're
                        ready to spot scams!
                      </p>
                    ) : (
                      <p className="result-message">
                        Good try! The correct red flags are highlighted in
                        green. Keep practicing!
                      </p>
                    )}
                    <button
                      className="challenge-restart-btn"
                      onClick={startChallenge}
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Common Scam Types */}
      <section className="scam-types-section">
        <div className="scam-types-container">
          <h2 className="section-title">
            <span className="icon">🛡️</span> Common Scam Types We Protect
            Against
          </h2>
          <div className="scam-types-grid">
            {scamTypes.map((scam) => (
              <div
                key={scam.id}
                className={`scam-card ${
                  expandedScam === scam.id ? "expanded" : ""
                }`}
                onClick={() =>
                  setExpandedScam(expandedScam === scam.id ? null : scam.id)
                }
              >
                <div className="scam-card-front">
                  <div className="scam-icon">{scam.icon}</div>
                  <h3 className="scam-title">{scam.title}</h3>
                  <p className="scam-description">{scam.description}</p>
                  <div className="expand-hint">
                    {expandedScam === scam.id
                      ? "Click to close"
                      : "Click to see examples"}
                  </div>
                </div>
                {expandedScam === scam.id && (
                  <div className="scam-card-back">
                    <h4>Common Examples:</h4>
                    <ul>
                      {scam.examples.map((example, idx) => (
                        <li key={idx}>{example}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <h2 className="section-title">
            <span className="icon">💬</span> Real People, Real Protection
          </h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-video-placeholder">
                <div className="play-button">▶</div>
              </div>
              <p className="testimonial-text">
                "I almost fell for a fake Amazon email. This training saved me
                $2,500."
              </p>
              <div className="testimonial-author">
                <strong>Margaret T.</strong>
                <span>Retired Teacher, 67</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-video-placeholder">
                <div className="play-button">▶</div>
              </div>
              <p className="testimonial-text">
                "My mom can now spot scam texts instantly. The practice
                scenarios are incredibly realistic."
              </p>
              <div className="testimonial-author">
                <strong>David K.</strong>
                <span>Software Engineer, 34</span>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-video-placeholder">
                <div className="play-button">▶</div>
              </div>
              <p className="testimonial-text">
                "Finally, a tool that teaches without being condescending. My
                whole family uses it now."
              </p>
              <div className="testimonial-author">
                <strong>Patricia R.</strong>
                <span>Small Business Owner, 52</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta-section">
        <div className="final-cta-container">
          <h2>Don't Wait Until It's Too Late</h2>
          <p>
            Join thousands of families who've learned to recognize and avoid
            scams before they strike.
          </p>
          <button className="cta-primary large" onClick={onManageAccountClick}>
            Start Your Free Trial Today
          </button>
          <p className="cta-subtext">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
}

export default LandingPageDemo;
